import type React from 'react';
import { AnyAction } from 'redux';
import { Description, Section } from '../../components/Help/Manual/types';
import { QuestionnaireItem } from '../../components/Cases/CaseInformation';

export interface SidePanelAction {
  label: string;
  variant: "outline" | "success" | "warning" | "neutral" | "primary" | "secondary" | "tertiary" | "danger" | "disabled" | "destructive" | undefined;
  size?: 'small';
  onClick: (instance?: any) => void;
}

interface CommonSidePanelState {
  showSidePanel: boolean;
  width?: string;
  isExpanded?: boolean;
  onConfirm?: (data?: any) => void;
  onCancel?: () => void;
  loading?: boolean;
  message?: string;
  icon?: any;
  subMsg?: string;
  actions?: SidePanelAction[];
  mb?: boolean;
}

interface CollaboratorsPanelState extends CommonSidePanelState {
  type: 'collaboratorsPanel';
  caseId: string;
  summary: any;
  onClose: () => void;
  onUpdate: () => void;
}

interface QuestionnairePanelState extends CommonSidePanelState {
  type: 'questionnairePanel';
  matterId: string;
  questionIndex: number;
  item?: QuestionnaireItem;
  onClose: () => void;
  onUpdate: () => void;
}


interface ReaderTypeState extends CommonSidePanelState {
  type: 'reader';
  citation: any;
  matterId: string;
  sourceType: string;
  userAccessLevel?: 'edit' | 'admin' | 'view';
  onDelete?: () => void;
}

interface DraftTypeState extends CommonSidePanelState {
  type: 'draftTypes';
  caseId: string;
  onSave?: () => void;
}

interface DraftTypeFilesState extends CommonSidePanelState {
  type: 'draftTypeFiles';
  caseId: string;
  draftTypeName: string;
}

interface DraftSettingsState extends CommonSidePanelState {
  type: 'draftSettings';
  matterId: string;
  draftId: number;
  onSave?: () => void;
}

interface WorkflowState extends CommonSidePanelState {
  type: 'workflows';
  caseId: string;
  onSave?: () => void;
}


interface ExtractedContentSidePanelState extends CommonSidePanelState {
  type: 'extractedContent';
  content: string;
  filename: string;
}

interface EmployeeTaskDetailSidePanelState extends CommonSidePanelState {
  type: 'employeeTaskDetail';
  taskData: any;
}

interface EmployeeCaseDetailSidePanelState extends CommonSidePanelState {
  type: 'employeeCaseDetail';
  caseData: any;
}

interface ProposeCaseSidePanelState extends CommonSidePanelState {
  type: 'proposeCase';
  lawyerId: string;
}

interface AddChronologyEventSidePanelState extends CommonSidePanelState {
  type: 'addChronologyEvent';
  caseId: string;
  onSuccess?: () => void;
}

interface AddLegalOutlineTemplateState extends CommonSidePanelState {
  type: 'addLegalOutlineTemplate';
  caseId: string;
  onFinish: () => void;
}

interface CreateIndexState extends CommonSidePanelState {
  type: 'createIndex';
}

interface LegalOutlineContentSidePanelState extends CommonSidePanelState {
  type: 'legalOutlineContent';
  sectionName: string;
  content: string;
  fieldData: { text: string; files: File[] };
  onSave: (newContent: string | null, newFiles: File[]) => void;
}

interface LegalOutlineGeneratedContentSidePanelState extends CommonSidePanelState {
  type: 'legalOutlineGeneratedContent';
  sectionName: string;
  content: string;
}

interface ChronologyFileContentSidePanelState extends CommonSidePanelState {
  type: 'chronologyFileContent';
  content: string;
  label: string;
}

interface FileReaderSidePanelState extends CommonSidePanelState {
  type: 'fileReader';
  fileUrl: string;
  fileType: string;
  loading: boolean;
}

interface FileReaderTstSidePanelState extends CommonSidePanelState {
  type: 'fileReaderTst';
  fileUrl?: string;
  content?: string;
  isPdf?: boolean;
  filename: string;
  loading: boolean;
}

interface SupportTicketFormSidePanelState extends CommonSidePanelState {
  type: 'supportTicketForm';
}

interface PlaceBidSidePanelState extends CommonSidePanelState {
  type: 'placeBid';
  engagement: any;
}

interface AddTaskSidePanelState extends CommonSidePanelState {
  type: 'addTask';
}


interface SupportTicketSidePanelState extends CommonSidePanelState {
  type: 'supportTicket';
}

interface HelpContentSidePanelState extends CommonSidePanelState {
  type: 'helpContent';
  content: {
    section: Section;
    description: Description;
  };
}

interface OnboardingSidePanelState extends CommonSidePanelState {
  type: 'onboarding';
}

interface CaseViewSidePanelState extends CommonSidePanelState {
  type: 'caseView';
  caseItem: any;
}

interface CustomSidePanelState extends CommonSidePanelState {
  type: 'custom';
}

interface AddClientSidePanelState extends CommonSidePanelState {
  type: 'addClient';
  onClientCreated?: (client: any) => void;
}

interface TimekeepingSidePanelState extends CommonSidePanelState {
  type: 'timekeepingView';
  day: string | null;
  data: any;
  isLoading: boolean;
  stats: {
    clocked_hours: number;
    negative_hours: number;
    overtime: number;
    total_hours_worked: number;
    tasks: string[];
    casesWorkedOn: number;
  } | null;
}

interface CreateProjectSidePanelState extends CommonSidePanelState {
  type: 'createProject';
}

interface AddCaseSidePanelState extends CommonSidePanelState {
  type: 'addCase';
  clientId: string | undefined;
  clientUserType: string | undefined;
}

interface PublishCasePanelState extends CommonSidePanelState {
  type: 'publishCase';
  caseItem: any;
}

interface AddTeamPanelState extends CommonSidePanelState {
  type: 'addTeam';
}

interface TimeEntryDetailsSidePanelState extends CommonSidePanelState {
  type: 'timeEntryDetails';
  data: any;
}

interface TransationDetails extends CommonSidePanelState {
  type: 'transactionDetails';
  transaction_id: string;
}

interface DocumentCustomizationSidePanelState extends CommonSidePanelState {
  type: 'documentCustomization';
  matterUuid: string;
  documentTypeId: string;
  documentType: 'draft' | 'insight';
  documentName: string;
}

interface LicenseDetailSidePanelState extends CommonSidePanelState {
  type: 'licenseDetail';
  plan: any;
  billingPeriod: 'monthly' | 'annual';
  isCurrentPlan: boolean;
  isActive: boolean;
  onAction?: (plan: any) => void;
  currentPlanSortOrder?: number;
  seatCount?: number;
  seatsUsed?: number;
  onUpdateSeats?: (newQuantity: number) => Promise<void>;
}

interface AssignLicenseSidePanelState extends CommonSidePanelState {
  type: 'assignLicense';
  userIds: number[];
  userNames: string[];
  onComplete?: () => void;
}

interface ProjectResourcesSidePanelState extends CommonSidePanelState {
  type: 'projectResources';
  projectUuid: string;
  onResourceLinked?: () => void;
}

interface UploadAttachmentSidePanelState extends CommonSidePanelState {
  type: 'uploadAttachment';
  files?: File[];
}


interface AddSourceSidePanelState extends CommonSidePanelState {
  type: 'addSource';
  sourceType: 'law' | 'case';
}

interface AddClientToMatterSidePanelState extends CommonSidePanelState {
  type: 'addClientToMatter';
  matterUuid: string;
  allClients: any[];
  clientRoleOptions: any[];
  selectedClients: string[];
  onRefresh?: () => void | Promise<void>;
  onAfterSave?: () => void | Promise<void>;
}

interface AddTableDocumentSidePanelState extends CommonSidePanelState {
  type: 'addTableDocument';
  tableUuid: string;
}

interface SaveTableTemplateSidePanelState extends CommonSidePanelState {
  type: 'saveTableTemplate';
  tableUuid: string;
  defaultName: string;
}

interface ResourceCollaboratorsSidePanelState extends CommonSidePanelState {
  type: 'manageResourceCollaborators';
  resourceUuid: string;
  resourceType: string;
  collaborators: any[];
  currentUserUuid?: string;
  onUpdate?: () => void;
  canAdmin?: boolean;
  addCollaborator: (resourceUuid: string, userUuid: string, access: string) => Promise<any>;
  removeCollaborator: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  updateAccess: (resourceUuid: string, collaboratorUuid: string, access: string) => Promise<any>;
  // Header customization
  title?: string;
  subMessage?: string;
  icon?: React.ReactNode;
  // Lead extension (clients use this)
  supportsLead?: boolean;
  isLead?: (collab: any) => boolean;
  setLead?: (resourceUuid: string, collaboratorUuid: string) => Promise<any>;
  ownerLabel?: string;
}

interface ClauseRuleFormPanelState extends CommonSidePanelState {
  type: 'editClauseRule';
  reviewUuid: string;
  rule?: any;
}

interface GlobalRuleFormPanelState extends CommonSidePanelState {
  type: 'editGlobalRule';
  reviewUuid: string;
  rule?: any;
}

export type SidePanelState =
  | AddTaskSidePanelState
  | SupportTicketSidePanelState
  | HelpContentSidePanelState
  | OnboardingSidePanelState
  | CaseViewSidePanelState
  | AddClientSidePanelState
  | CustomSidePanelState
  | TimekeepingSidePanelState
  | CreateProjectSidePanelState
  | AddCaseSidePanelState
  | PublishCasePanelState
  | AddTeamPanelState
  | PlaceBidSidePanelState
  | ProposeCaseSidePanelState
  | SupportTicketFormSidePanelState
  | FileReaderSidePanelState
  | LegalOutlineContentSidePanelState
  | LegalOutlineGeneratedContentSidePanelState
  | ChronologyFileContentSidePanelState
  | AddChronologyEventSidePanelState
  | AddLegalOutlineTemplateState
  | CreateIndexState
  | TimeEntryDetailsSidePanelState
  | EmployeeTaskDetailSidePanelState
  | EmployeeCaseDetailSidePanelState
  | ExtractedContentSidePanelState
  | TransationDetails
  | WorkflowState
  | FileReaderTstSidePanelState
  | DraftTypeState
  | DraftTypeFilesState
  | DraftSettingsState
  | ReaderTypeState
  | QuestionnairePanelState
  | CollaboratorsPanelState
  | DocumentCustomizationSidePanelState
  | LicenseDetailSidePanelState
  | AssignLicenseSidePanelState
  | ProjectResourcesSidePanelState
  | UploadAttachmentSidePanelState
  | AddSourceSidePanelState
  | AddClientToMatterSidePanelState
  | AddTableDocumentSidePanelState
  | SaveTableTemplateSidePanelState
  | ResourceCollaboratorsSidePanelState
  | ClauseRuleFormPanelState
  | GlobalRuleFormPanelState;

const initialState: SidePanelState = {
  showSidePanel: false,
  type: 'custom',
  width: '50%',
  isExpanded: false,
  loading: false,
  message: '',
  mb: true
};

export const sidePanelReducer = (
  state = initialState,
  action: AnyAction
): SidePanelState => {
  switch (action.type) {
    case 'SHOW_SIDE_PANEL':
      return {
        ...state,
        ...action.payload,
        showSidePanel: true,
        loading: action.payload.type === 'fileReader' || action.payload.type === 'fileReaderTst' ? true : state.loading,
      };
    case 'HIDE_SIDE_PANEL':
      return {
        ...initialState,
        showSidePanel: false,
        isExpanded: false,
        loading: false,
      };
    case 'EXPAND_SIDE_PANEL':
      return {
        ...state,
        isExpanded: true,
        width: '85%',
      };
    case 'COLLAPSE_SIDE_PANEL':
      return {
        ...state,
        isExpanded: false,
        width: '50%',
      };
    case 'UPDATE_SIDE_PANEL':
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

export default sidePanelReducer;
